import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Plus, Pencil, KeyRound, Trash2, Users } from 'lucide-react';
import apiService, { AdminUser } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import StatusPill from './ui/StatusPill';
import EmptyState from './ui/EmptyState';
import { CAPABILITIES, CAPABILITY_KEYS, type Capability } from '../../config/capabilities';

type Role = 'admin' | 'bearbeiter';

interface FormState {
  id?: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  language: string;
  role: Role;
  isActive: boolean;
  password: string;
  permissions: Capability[];
}

// Standard für neu angelegte Bearbeiter: alle Ansicht-Bereiche, aber KEINE
// Schreib-/Sende-Rechte (invoices-manage: Belege erstellen, communications:
// Mails senden). Der Admin kann das im Dialog anpassen.
const DEFAULT_NEW_PERMISSIONS: Capability[] = CAPABILITY_KEYS.filter(
  (c) => c !== 'invoices-manage' && c !== 'communications'
);

const EMPTY_FORM: FormState = {
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  language: 'de',
  role: 'bearbeiter',
  isActive: true,
  password: '',
  permissions: DEFAULT_NEW_PERMISSIONS,
};

const inputClass =
  'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';

const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { user: me } = useAuth();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pwUserId, setPwUserId] = useState<number | null>(null);
  const [pwValue, setPwValue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiService.get<AdminUser[]>('/users');
      if (resp.data?.success) setUsers(resp.data.data || []);
      else addToast(resp.data?.message || t('users.errorLoad'), 'error');
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('users.errorLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast, t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setShowModal(true);
  };
  const openEdit = (u: AdminUser) => {
    setForm({
      id: u.id,
      username: u.username,
      email: u.email,
      firstName: u.first_name,
      lastName: u.last_name,
      language: u.language || 'de',
      role: u.role,
      isActive: u.is_active,
      password: '',
      permissions: (u.permissions || []).filter((p): p is Capability =>
        (CAPABILITY_KEYS as string[]).includes(p)),
    });
    setShowModal(true);
  };

  // Schalter für einen Bereich umlegen. Abhängigkeiten beachten:
  // - invoices-manage setzt invoices voraus → beim Aktivieren invoices mitnehmen.
  // - invoices deaktivieren → invoices-manage fällt mit weg.
  const togglePermission = (cap: Capability, checked: boolean) => {
    setForm((prev) => {
      let next = new Set(prev.permissions);
      if (checked) {
        next.add(cap);
        const def = CAPABILITIES.find((c) => c.key === cap);
        if (def?.requires) next.add(def.requires);
      } else {
        next.delete(cap);
        // Abhängige Rechte entfernen, die dieses cap voraussetzen.
        CAPABILITIES.filter((c) => c.requires === cap).forEach((c) => next.delete(c.key));
      }
      // In kanonischer Reihenfolge ablegen.
      return { ...prev, permissions: CAPABILITY_KEYS.filter((k) => next.has(k)) };
    });
  };
  const closeModal = () => {
    setShowModal(false);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.username || !form.email || !form.firstName || !form.lastName) {
      addToast(t('users.errorRequiredFields'), 'error');
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        const resp = await apiService.put<AdminUser>(`/users/${form.id}`, {
          username: form.username,
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          language: form.language,
          role: form.role,
          isActive: form.isActive,
          permissions: form.role === 'admin' ? [] : form.permissions,
        });
        if (resp.data?.success) {
          addToast(t('users.userUpdated'), 'success');
          closeModal();
          await load();
        } else {
          addToast(resp.data?.message || t('users.errorSave'), 'error');
        }
      } else {
        if (!form.password || form.password.length < 8) {
          addToast(t('users.errorPasswordLength'), 'error');
          setSaving(false);
          return;
        }
        const resp = await apiService.post<AdminUser>('/users', {
          username: form.username,
          email: form.email,
          password: form.password,
          firstName: form.firstName,
          lastName: form.lastName,
          language: form.language,
          role: form.role,
          isActive: form.isActive,
          permissions: form.role === 'admin' ? [] : form.permissions,
        });
        if (resp.data?.success) {
          addToast(t('users.userCreated'), 'success');
          closeModal();
          await load();
        } else {
          addToast(resp.data?.message || t('users.errorCreate'), 'error');
        }
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('users.errorSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u: AdminUser) => {
    if (u.id === me?.id) {
      addToast(t('users.errorDeleteSelf'), 'error');
      return;
    }
    if (!window.confirm(t('users.confirmDelete', { username: u.username }))) return;
    try {
      const resp = await apiService.delete<any>(`/users/${u.id}`);
      if (resp.data?.success) {
        addToast(t('users.userDeleted'), 'success');
        await load();
      } else {
        addToast(resp.data?.message || t('users.errorDelete'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('users.errorDelete'), 'error');
    }
  };

  const handlePasswordReset = async () => {
    if (pwUserId == null) return;
    if (!pwValue || pwValue.length < 8) {
      addToast(t('users.errorPasswordLength'), 'error');
      return;
    }
    try {
      const resp = await apiService.patch<any>(`/users/${pwUserId}/password`, { password: pwValue });
      if (resp.data?.success) {
        addToast(t('users.passwordSet'), 'success');
        setPwUserId(null);
        setPwValue('');
      } else {
        addToast(resp.data?.message || t('users.errorPasswordReset'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('users.errorPasswordReset'), 'error');
    }
  };

  return (
    <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between p-5 border-b border-brand-border">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-brand-text">{t('users.title')}</h2>
          <p className="text-sm text-brand-text-muted mt-1">
            <Trans i18nKey="users.roleExplanation" components={{ b: <span className="font-semibold text-brand-text" /> }} />
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 shrink-0"
        >
          <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
          {t('users.newUser')}
        </button>
      </header>

      {loading ? (
        <div className="p-12 text-center text-sm text-brand-text-muted">{t('users.loading')}</div>
      ) : users.length === 0 ? (
        <EmptyState
          icon={Users}
          title={t('users.emptyTitle')}
          description={t('users.emptyDescription')}
        />
      ) : (
        <>
          {/* Desktop: table */}
          <div className="hidden md:block">
            <table className="w-full table-fixed divide-y divide-brand-border">
              <colgroup>
                <col className="w-[28%]" />
                <col className="w-[32%]" />
                <col className="w-[14%]" />
                <col className="w-[12%]" />
                <col className="w-[14%]" />
              </colgroup>
              <thead className="bg-brand-surface-2/60">
                <tr>
                  <Th>{t('users.colUser')}</Th>
                  <Th>{t('common.email')}</Th>
                  <Th>{t('users.role')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="right"><span className="sr-only">{t('common.actions')}</span></Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {users.map(u => {
                  const isMe = u.id === me?.id;
                  return (
                    <tr key={u.id} className={`hover:bg-brand-surface-2/60 transition-colors ${isMe ? 'bg-brand-accent/5' : ''}`}>
                      <td className="px-4 py-3 align-middle">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-accent/15 text-brand-primary text-xs font-semibold">
                            {(u.first_name?.[0] || '').toUpperCase()}{(u.last_name?.[0] || '').toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-brand-text truncate">
                              {u.first_name} {u.last_name}
                              {isMe && <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider text-brand-accent">{t('users.youBadge')}</span>}
                            </div>
                            <div className="text-xs text-brand-text-muted truncate">@{u.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="text-sm text-brand-text truncate" title={u.email}>{u.email}</div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <StatusPill tone={u.role === 'admin' ? 'accent' : 'neutral'}>
                          {u.role === 'admin' ? t('users.roleAdmin') : t('users.roleEditor')}
                        </StatusPill>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        {u.is_active
                          ? <StatusPill tone="success" dot>{t('common.active')}</StatusPill>
                          : <StatusPill tone="danger">{t('common.inactive')}</StatusPill>}
                      </td>
                      <td className="px-4 py-3 text-right align-middle">
                        <div className="inline-flex items-center gap-1">
                          <IconButton onClick={() => openEdit(u)} label={t('common.edit')} tone="primary">
                            <Pencil className="h-4 w-4" strokeWidth={1.75} />
                          </IconButton>
                          <IconButton
                            onClick={() => { setPwUserId(u.id); setPwValue(''); }}
                            label={t('users.setPassword')}
                            tone="primary"
                          >
                            <KeyRound className="h-4 w-4" strokeWidth={1.75} />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(u)}
                            label={isMe ? t('users.cannotDeleteSelf') : t('common.delete')}
                            tone="danger"
                            disabled={isMe}
                          >
                            <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: card list */}
          <ul className="md:hidden divide-y divide-brand-border">
            {users.map(u => {
              const isMe = u.id === me?.id;
              return (
                <li key={u.id} className={`p-4 ${isMe ? 'bg-brand-accent/5' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-accent/15 text-brand-primary text-sm font-semibold">
                      {(u.first_name?.[0] || '').toUpperCase()}{(u.last_name?.[0] || '').toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-brand-text">
                        {u.first_name} {u.last_name}
                        {isMe && <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider text-brand-accent">{t('users.youBadge')}</span>}
                      </div>
                      <div className="text-xs text-brand-text-muted">@{u.username}</div>
                      <div className="text-xs text-brand-text-muted truncate mt-0.5" title={u.email}>{u.email}</div>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <StatusPill tone={u.role === 'admin' ? 'accent' : 'neutral'}>
                          {u.role === 'admin' ? t('users.roleAdmin') : t('users.roleEditor')}
                        </StatusPill>
                        {u.is_active
                          ? <StatusPill tone="success" dot>{t('common.active')}</StatusPill>
                          : <StatusPill tone="danger">{t('common.inactive')}</StatusPill>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <IconButton onClick={() => openEdit(u)} label={t('common.edit')} tone="primary">
                        <Pencil className="h-4 w-4" strokeWidth={1.75} />
                      </IconButton>
                      <IconButton
                        onClick={() => { setPwUserId(u.id); setPwValue(''); }}
                        label={t('users.setPassword')}
                        tone="primary"
                      >
                        <KeyRound className="h-4 w-4" strokeWidth={1.75} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(u)}
                        label={isMe ? t('users.cannotDeleteSelf') : t('common.delete')}
                        tone="danger"
                        disabled={isMe}
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                      </IconButton>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/* Create / Edit modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-brand-surface text-brand-text rounded-xl shadow-xl border border-brand-border w-full max-w-lg p-6 space-y-4"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-brand-text">
              {form.id ? t('users.editUser') : t('users.createUser')}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>{t('users.firstName')}</label>
                <input className={inputClass} value={form.firstName}
                  onChange={e => setForm({ ...form, firstName: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>{t('users.lastName')}</label>
                <input className={inputClass} value={form.lastName}
                  onChange={e => setForm({ ...form, lastName: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>{t('users.username')}</label>
                <input className={inputClass} value={form.username}
                  onChange={e => setForm({ ...form, username: e.target.value })} autoComplete="off" />
              </div>
              <div>
                <label className={labelClass}>{t('users.emailRequired')}</label>
                <input type="email" className={inputClass} value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })} autoComplete="off" />
              </div>
              <div>
                <label className={labelClass}>{t('users.role')}</label>
                <select
                  className={inputClass}
                  value={form.role}
                  onChange={e => setForm({ ...form, role: e.target.value as Role })}
                  disabled={form.id === me?.id}
                  title={form.id === me?.id ? t('users.cannotChangeOwnRole') : ''}
                >
                  <option value="bearbeiter">{t('users.roleEditor')}</option>
                  <option value="admin">{t('users.roleAdmin')}</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>{t('users.languageLabel')}</label>
                <select className={inputClass} value={form.language}
                  onChange={e => setForm({ ...form, language: e.target.value })}>
                  <option value="de">Deutsch</option>
                  <option value="es">Español</option>
                  <option value="en">English</option>
                  <option value="ru">Русский</option>
                </select>
              </div>
              {!form.id && (
                <div className="sm:col-span-2">
                  <label className={labelClass}>{t('users.passwordRequired')}</label>
                  <input type="password" className={inputClass}
                    value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                    autoComplete="new-password" />
                </div>
              )}
              <label className="sm:col-span-2 flex items-center gap-2 text-sm text-brand-text cursor-pointer">
                <input type="checkbox"
                  checked={form.isActive}
                  onChange={e => setForm({ ...form, isActive: e.target.checked })}
                  disabled={form.id === me?.id}
                  className="rounded border-brand-border text-brand-primary focus:ring-brand-primary/40"
                />
                <span>{t('users.activeLoginAllowed')}</span>
              </label>

              {/* Feingranulare Bereichsrechte — nur für Bearbeiter relevant.
                  Admins haben implizit Zugriff auf alle Bereiche. */}
              {form.role === 'bearbeiter' ? (
                <div className="sm:col-span-2">
                  <label className={labelClass}>{t('users.permissionsLabel')}</label>
                  <p className="text-xs text-brand-text-muted mb-2">{t('users.permissionsHint')}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 rounded-md border border-brand-border p-3">
                    {CAPABILITIES.map(cap => (
                      <label key={cap.key} className="flex items-start gap-2 text-sm text-brand-text cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.permissions.includes(cap.key)}
                          onChange={e => togglePermission(cap.key, e.target.checked)}
                          className="mt-0.5 rounded border-brand-border text-brand-primary focus:ring-brand-primary/40"
                        />
                        <span>
                          {t(cap.i18nKey)}
                          {cap.hintKey && (
                            <span className="block text-[11px] text-brand-text-muted">{t(cap.hintKey)}</span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="sm:col-span-2 rounded-md bg-brand-accent/10 border border-brand-accent/30 px-3 py-2 text-xs text-brand-primary">
                  {t('users.permissionsAdminNote')}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-brand-border">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium border border-brand-border rounded-md text-brand-text hover:bg-brand-surface-2 transition-colors cursor-pointer"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover transition-colors disabled:opacity-50 cursor-pointer shadow-sm"
              >
                {saving ? t('users.saving') : (form.id ? t('common.save') : t('users.create'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password reset modal */}
      {pwUserId != null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setPwUserId(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-brand-surface text-brand-text rounded-xl shadow-xl border border-brand-border w-full max-w-md p-6 space-y-4"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-brand-text">{t('users.setPassword')}</h3>
            <p className="text-sm text-brand-text-muted">
              <Trans
                i18nKey="users.setPasswordHint"
                values={{ username: users.find(u => u.id === pwUserId)?.username }}
                components={{ b: <span className="font-semibold text-brand-text" /> }}
              />
            </p>
            <input
              type="password"
              className={inputClass}
              placeholder={t('users.newPasswordPlaceholder')}
              autoComplete="new-password"
              value={pwValue}
              onChange={e => setPwValue(e.target.value)}
            />
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-brand-border">
              <button
                onClick={() => setPwUserId(null)}
                className="px-4 py-2 text-sm font-medium border border-brand-border rounded-md text-brand-text hover:bg-brand-surface-2 transition-colors cursor-pointer"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handlePasswordReset}
                className="px-4 py-2 text-sm font-medium bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover transition-colors cursor-pointer shadow-sm"
              >
                {t('users.setButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <th className={`px-4 py-3 text-${align} text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted`}>
    {children}
  </th>
);

const IconButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  tone: 'primary' | 'danger';
  disabled?: boolean;
}> = ({ children, onClick, label, tone, disabled }) => {
  const toneClass =
    tone === 'danger'
      ? 'text-brand-text-muted hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 dark:hover:text-rose-400'
      : 'text-brand-text-muted hover:text-brand-primary hover:bg-brand-accent/10';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${
        disabled
          ? 'text-brand-text-muted/40 cursor-not-allowed'
          : `cursor-pointer ${toneClass}`
      }`}
    >
      {children}
    </button>
  );
};

export default UserManagement;
